export type MatchStatus = "discovered" | "watching" | "done" | "failed";

export type ParseStage =
  | "awaiting_manifest"
  | "parsing_data"
  | "awaiting_chunks"
  | "parsing_events";

export interface MatchProgress {
  stage: ParseStage;
  data_chunks: number;
  data_total: number;
  event_chunks: number;
  event_total: number;
  roster: number;
  elims: number;
  live: boolean;
  elapsed_s: number;
  idle_s: number;
}

export interface WindowInfo {
  event_id: string;
  event_window_id: string;
  mode: "roster" | "full";
  /** "" | "watch" | "backfill" — manual pin kind, if any */
  pinned: string;
  /** discovered sessions are auto-started */
  discovery: boolean;
  /** complete-the-window mode: everything gets parsed */
  backfilling: boolean;
  discovered: number;
  watching: number;
  done: number;
  failed: number;
}

export interface TrackedBase {
  event_base: string;
  mode: "roster" | "full";
}

export interface Permits {
  total: number;
  in_use: number;
}

export type CaptureStatus = "untracked" | "discovered" | "watching" | "done" | "failed";

export interface MatchSummary {
  session_id: string;
  event_id: string;
  event_window_id: string;
  status: MatchStatus;
  player_count: number;
  elims?: number;
  detail?: string;
  updated_at?: string;
  progress?: MatchProgress;
}

export interface StatusResponse {
  active_watchers: number;
  queued: number;
  permits: Permits;
  tracked: TrackedBase[];
  windows: WindowInfo[];
  matches: MatchSummary[];
}

export interface Player {
  epic_id: string;
  display_name: string;
  is_bot: boolean;
}

export interface MatchDetail {
  session_id: string;
  event_id: string;
  event_window_id: string;
  status: MatchStatus;
  chunks_parsed?: number;
  players?: Player[];
  detail?: string;
  progress?: MatchProgress;
}

export interface EventWindow {
  event_window_id: string;
  begin_time: string;
  end_time: string;
  round: number;
}

export interface DisplayInfo {
  title_line_1: string;
  title_line_2: string;
  short_title: string;
  schedule_info: string;
  tile_image: string;
  poster_image: string;
  primary_color: string;
  base_color: string;
}

export interface EventInfo {
  event_id: string;
  begin_time: string;
  end_time: string;
  display: DisplayInfo | null;
  windows: EventWindow[];
}

export interface RegionEvents {
  region: string;
  events: EventInfo[];
}

export interface StoredMatch {
  session_id: string;
  event_id: string;
  event_window_id: string;
  player_count: number;
  chunks_parsed: number;
  completed_at: string;
}

// ---- candidates + watch set ----

/** A pickable pro: the curated allowlist clients may watch. */
export interface Candidate {
  epic_id: string;
  username: string;
  region: string;
  is_verified_pro: boolean;
}

// ---- the watch-party view ----

export type PlayerLiveState = "alive" | "knocked" | "eliminated";

export interface TrackedLivePlayer {
  epic_id: string;
  display_name: string;
  /** one of the players this client is actually watching (vs a surfaced teammate) */
  watched: boolean;
  state: PlayerLiveState;
  /** match-clock ms of the knock/elim, null while alive */
  state_since_ms: number | null;
  elims: number;
}

export interface TrackedLiveTeam {
  alive: boolean;
  elims: number;
  teams_remaining: number;
  projected_placement: number;
  projected_points: number;
}

export interface TrackedLive {
  session_id: string;
  /** "epic" — board carried a liveSessionId; "roster" — we found them in a parsed roster */
  source: "epic" | "roster";
  roster_confirmed: boolean;
  capture_status: CaptureStatus;
  capture_mode: string;
  team: TrackedLiveTeam | null;
  players: TrackedLivePlayer[];
}

export interface TrackedHistoryRow {
  session_id: string;
  placement: number;
  team_elims: number;
  end_time: string | null;
  source: "official" | "replay";
  capture_status: CaptureStatus;
}

/** A member of a synthesised team — `watched` marks the ones this client
 *  picked (vs teammates surfaced automatically from the board). */
export interface TrackedMember {
  epic_id: string;
  display_name: string;
  watched: boolean;
}

export interface TrackedTeam {
  /** synthesised from member names, e.g. "Peterbot + Pollo" */
  label: string;
  team_id: string | null;
  rank: number | null;
  points: number | null;
  members: TrackedMember[];
  live: TrackedLive | null;
  history: TrackedHistoryRow[];
}

export interface WindowTrackedResponse {
  event_window_id: string;
  region: string;
  teams: TrackedTeam[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error ?? `request failed (${res.status})`);
  }
  return body as T;
}

export const api = {
  status: () => request<StatusResponse>("/api/status"),

  match: (sessionId: string) => request<MatchDetail>(`/api/matches/${sessionId}`),

  events: () => request<{ regions: RegionEvents[] }>("/api/events"),

  archiveMatches: () => request<{ matches: StoredMatch[] }>("/api/archive/matches"),

  candidates: (q = "") =>
    request<{ candidates: Candidate[] }>(
      `/api/candidates${q ? `?q=${encodeURIComponent(q)}` : ""}`,
    ),

  // Admin (client-gated): add a verified pro to the allowlist so they
  // become watchable and appear in the picker. Re-adding an existing
  // epic_id just re-labels/re-enables them.
  addCandidate: (epicId: string, username: string, region: string) =>
    request<Candidate>("/api/candidates", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ epic_id: epicId, username, region }),
    }),

  // Watching is the only watch-set write the public UI makes: it adds the
  // pick to localStorage and joins the server capture set. Unwatch is
  // browser-only and the global set is operator-curated (DELETE /api/watch
  // exists for that admin/manual trim, not the UI).
  watch: (epicId: string) =>
    request<{ watching: string; username: string }>("/api/watch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ epic_id: epicId }),
    }),

  windowTracked: (windowId: string, ids?: string[]) =>
    request<WindowTrackedResponse>(
      `/api/windows/${encodeURIComponent(windowId)}/tracked${
        ids && ids.length ? `?ids=${encodeURIComponent(ids.join(","))}` : ""
      }`,
    ),

  track: (eventBase: string, mode: "roster" | "full" = "roster") =>
    request<{ tracking: string }>("/api/track", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ event_base: eventBase, mode }),
    }),

  untrack: (eventBase: string) =>
    request<{ stopped: string }>(`/api/track/${encodeURIComponent(eventBase)}`, {
      method: "DELETE",
    }),

  parseSession: (sessionId: string, eventId: string, windowId: string) =>
    request<{ parsing: string }>(`/api/sessions/${encodeURIComponent(sessionId)}/parse`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ event_id: eventId, event_window_id: windowId }),
    }),

  watchWindow: (windowId: string, eventId: string, mode: "roster" | "full" = "roster") =>
    request<{ watching: string }>(`/api/windows/${encodeURIComponent(windowId)}/watch`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ event_id: eventId, mode }),
    }),

  unwatchWindow: (windowId: string) =>
    request<{ stopped: string }>(`/api/windows/${encodeURIComponent(windowId)}/watch`, {
      method: "DELETE",
    }),

  backfillWindow: (windowId: string, eventId: string) =>
    request<{ backfilling: string; queued_now: number }>(
      `/api/windows/${encodeURIComponent(windowId)}/backfill`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ event_id: eventId }),
      },
    ),
};
